import { useEffect, useContext } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useUI } from "../../contexts/UIContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAPI } from "../../hooks/useAPI";
import { BrandContext } from "../../App";

import PageTitle from "../../components/PageTitle/PageTitle";
import CoachCard from "../../components/CoachCard/CoachCard";

import VideoAvatar from "../../components/VideoAvatar/VideoAvatar";

import { Box, Alert, CircularProgress } from "@mui/material";

const CoachHome = () => {
  const { setLayoutMode, theme } = useUI();
  const { callAPI } = useAPI();
  const { authToken } = useAuth();
  const { featureFlags } = useContext(BrandContext);
  const navigate = useNavigate();

  if (featureFlags?.on_demand_coach == false) {
    setLayoutMode("full");
    navigate("/dashboard");
  }
  console.log(featureFlags?.on_demand_coach);

  useEffect(() => {
    setLayoutMode("minimal");
  }, [setLayoutMode]);

  const {
    data: coachesData = [],
    isLoading: isCoachesLoading,
    error: coachesError,
  } = useQuery({
    queryKey: ["on_demand_coaches"],
    queryFn: () =>
      callAPI(`on_demand_coach`, {
        token: authToken,
      }),
    staleTime: 1000 * 60 * 5,
  });

  const startSimulationMutation = useMutation({
    mutationFn: (id) =>
      callAPI(`start_on_demand_coaching_simulation`, {
        method: "POST",
        token: authToken,
        body: { scenario_id: id },
      }),
  });

  const startingCoachId = startSimulationMutation.isPending
    ? startSimulationMutation.variables
    : null;

  const simulationData = startSimulationMutation.data;

  return (
    <div id="on-demand-coach-dashboard">
      <div className="on-demand-badge">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.58685 5.90216C6.05085 6.86857 6.68337 7.77433 7.48443 8.57538C8.28548 9.37644 9.19124 10.009 10.1577 10.473C10.2408 10.5129 10.2823 10.5328 10.3349 10.5482C10.5218 10.6026 10.7513 10.5635 10.9096 10.4502C10.9542 10.4183 10.9923 10.3802 11.0685 10.304C11.3016 10.0709 11.4181 9.95435 11.5353 9.87816C11.9772 9.59083 12.5469 9.59083 12.9889 9.87816C13.106 9.95435 13.2226 10.0709 13.4556 10.304L13.5856 10.4339C13.9398 10.7882 14.117 10.9653 14.2132 11.1556C14.4046 11.5339 14.4046 11.9808 14.2132 12.3592C14.117 12.5494 13.9399 12.7266 13.5856 13.0809L13.4805 13.1859C13.1274 13.539 12.9508 13.7156 12.7108 13.8504C12.4445 14 12.0308 14.1076 11.7253 14.1067C11.45 14.1059 11.2619 14.0525 10.8856 13.9457C8.86333 13.3717 6.95509 12.2887 5.36311 10.6967C3.77112 9.10472 2.68814 7.19648 2.11416 5.17421C2.00735 4.79792 1.95395 4.60977 1.95313 4.33447C1.95222 4.02899 2.0598 3.61533 2.20941 3.34899C2.34424 3.10897 2.52078 2.93243 2.87386 2.57935L2.97895 2.47426C3.33325 2.11996 3.5104 1.94281 3.70065 1.84658C4.07903 1.6552 4.52587 1.6552 4.90424 1.84658C5.0945 1.94281 5.27164 2.11996 5.62594 2.47426L5.75585 2.60417C5.98892 2.83724 6.10546 2.95377 6.18165 3.07096C6.46898 3.51289 6.46898 4.08261 6.18165 4.52453C6.10546 4.64172 5.98892 4.75826 5.75585 4.99133C5.67964 5.06753 5.64154 5.10564 5.60965 5.15018C5.4963 5.30847 5.45717 5.53797 5.51165 5.72487C5.52698 5.77747 5.54694 5.81903 5.58685 5.90216Z"
            stroke="#01ECFF"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>On-Demand</span>
      </div>
      <PageTitle text="Your Coaches">
        Select a coach to start an on-demand conversation
      </PageTitle>

      {startSimulationMutation.isError && (
        <Alert severity="error" sx={{ mt: 4 }}>
          {startSimulationMutation.error?.detail ||
            startSimulationMutation.error?.message ||
            "Failed to start simulation. Please try again."}
        </Alert>
      )}

      {!simulationData ? (
        <>
          {isCoachesLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                textAlign: "center",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                padding: "50px 0",
                boxShadow:
                  "0px 12px 16px -4px rgba(0, 91, 140, 0.10), 0px 4px 6px -2px rgba(0, 91, 140, 0.03), 0px 2px 2px -1px rgba(0, 91, 140, 0.04)",
                border: `2px solid`,
                borderColor: theme.palette.border.tertiary,
                borderRadius: "8px",
                "& span": {
                  opacity: ".8",
                },
              }}
            >
              <CircularProgress />
              <span>Loading Coaches...</span>
            </Box>
          ) : coachesError ? (
            <Alert severity="error" sx={{ mt: 4 }}>
              {coachesError.message ||
                coachesError.detail ||
                "An error occurred while loading coaches. Please try again later."}
            </Alert>
          ) : (
            <Box
              id="coach-cards-container"
              sx={{
                display: "grid",
                gap: "20px",
                mt: 4,
                gridTemplateColumns: {
                  xs: "repeat(1, 1fr)",
                  sm: "repeat(1, 1fr)",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
              }}
            >
              {coachesData.map((item, i) => (
                <CoachCard
                  key={`coach-${item.id}`}
                  data={item}
                  startSimulation={(id) => startSimulationMutation.mutate(id)}
                  startingCoachId={startingCoachId}
                />
              ))}
            </Box>
          )}
        </>
      ) : (
        <VideoAvatar
          scenario={null}
          config={simulationData}
          simulationType="on_demand_coach"
        />
      )}
    </div>
  );
};

export default CoachHome;
